import type {
    PaymentPlanType,
    QuoteFieldsFragment,
    WidgetFieldsFragment,
} from "~graphql/generated/graphql"
import {
    BookingInternalSource,
    BookingInternalSourceEntityType,
    useCreateBookingMutation,
} from "~graphql/generated/graphql"
import { trackPurchase } from "~utils/event-tracking/event-tracking-helpers"
import type { PaymentMethodState } from "~utils/use-payment-method-state"

import { getInternalSourceQueryParams } from "./url-param-helpers"

export type Props = {
    quote: QuoteFieldsFragment | null
    paymentPlanType: PaymentPlanType
    widget?: WidgetFieldsFragment | null
    onSuccess: (bookingId: string) => void
}

export default function useCreateBooking({
    quote,
    paymentPlanType,
    widget,
    onSuccess,
}: Props) {
    const [createBookingMutation, { loading: isLoading, error }] =
        useCreateBookingMutation({
            onCompleted: (data) => {
                const bookingId = data.createBooking.id
                trackPurchase({ quote: quote!, bookingId })
                onSuccess(bookingId)
            },
        })

    const createBooking = async ({
        paymentMethodId,
        isExternalPaymentMethod,
    }: PaymentMethodState) => {
        const sourceParams = getSourceParams({ quote, widget })

        await createBookingMutation({
            variables: {
                data: {
                    payment_method: paymentMethodId,
                    payment_plan_type: paymentPlanType,
                    quote_id: quote?.id ?? "",
                    widget_source: widget?.id,
                    is_external_payment_method: isExternalPaymentMethod,
                    ...sourceParams,
                },
            },
        })
    }

    return { createBooking, isLoading, error }
}

function getSourceParams({
    quote,
    widget,
}: {
    quote: QuoteFieldsFragment | null
    widget?: WidgetFieldsFragment | null
}) {
    return getInternalSourceQueryParams({
        defaultSource: widget
            ? BookingInternalSource.GuideTech
            : BookingInternalSource.MallardBay,
        defaultEntityId: widget?.id ?? quote?.listing.id,
        defaultEntityType: widget
            ? BookingInternalSourceEntityType.Widgets
            : BookingInternalSourceEntityType.Listings,
    })
}
